import React from 'react';
import useClearableState from '../../hooks/useClearableState';
import { fileToDataURI } from '../../utils/fileUtils';
import { uploadImg } from '../../utils';
import { ShowIf } from '../../flayr-components/ShowIf/ShowIf';
import { Img } from '../../flayr-components/Img/Img';
import { Spinner } from '../../flayr-components/Spinner/Spinner';

interface ImgUploaderProps {
  // container
  className?: string;
  showErrorMessage?: boolean;
  errorMessage?: string;

  // functions
  uploadFunction?: (e?: any) => any;
  renameFileFunction?: (e: File) => string;

  enableBuiltInImgUpload?: boolean;
  name?: string;
  disabled?: boolean;
  accept?: string;
  id?: string;
  maxFileSize?: number; // file size in mb

  // preview
  previewUploadingImg?: boolean;
  previewClassName?: string;

  // events
  onChange?: (e?: any) => void;
  onUpload?: (e?: any) => void;
  onUploadStart?: (e?: any) => void;
  onUploadError?: (e?: any) => void;
}

const ImgUploader: React.FC<React.PropsWithChildren<ImgUploaderProps>> = ({
  className = '',
  showErrorMessage,
  errorMessage = 'file size is too big.',

  enableBuiltInImgUpload,
  previewUploadingImg,
  name,
  disabled,
  accept = 'image/png, image/jpeg, image/heic',
  children,
  previewClassName = '',
  id,
  maxFileSize = 4.1,

  // functions
  uploadFunction,
  renameFileFunction,

  // events
  onUpload,
  onChange,
  onUploadStart,
  onUploadError,
}) => {
  const [uploadingImg, setUploadingImg, clearUploadingImg] =
    useClearableState<string>('');

  const [error, setError] = React.useState<string>('');

  async function handleChange(e) {
    const file: File = e.target.files[0];
    if (!file) {
      return;
    }
    const sizeInMb = file.size / 1024 ** 2;
    if (sizeInMb >= maxFileSize) {
      showErrorMessage && setError(errorMessage);
      return onUploadError?.(errorMessage);
    } else if (error) {
      setError('');
    }
    onUploadError?.('');

    onChange?.(e);

    if (enableBuiltInImgUpload || uploadFunction) {
      try {
        let previewUrl = '';
        if (previewUploadingImg) {
          previewUrl = await fileToDataURI(file);
          setUploadingImg((previewUrl as string) || '');
        }
        onUploadStart?.(previewUrl || file);
        const uploader = uploadFunction || uploadImg;
        const url = await uploader(file, id || 'undefined', {
          convertToWebp: false,
          resize: 900,
        });
        previewUploadingImg && clearUploadingImg();
        return onUpload?.(url);
      } catch (error: any) {
        setError(error?.message);
      }
    }
  }

  return (
    <label className={className}>
      <input
        className="hidden w-0 h-0 opacity-0 appearance-none"
        type="file"
        accept={accept}
        disabled={disabled || !!uploadingImg}
        name={name}
        onChange={handleChange}
      />
      <ShowIf
        condition={previewUploadingImg && uploadingImg}
        elseTemplate={children}
      >
        <div className={'relative ' + previewClassName}>
          <Img src={uploadingImg} size="square" grayScale />
          <Spinner
            className="absolute top-0 left-0 flex items-center justify-center w-full h-full text-xs text-white"
            showing={!!uploadingImg}
          />
        </div>
      </ShowIf>

      <ShowIf condition={error}>
        <div className="mt-2 text-sm text-red-500">{error}</div>
      </ShowIf>
    </label>
  );
};
export default ImgUploader;
